import React, { useCallback, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { prism } from 'react-syntax-highlighter/dist/esm/styles/prism';
import {
  RightOutlined,
  DownOutlined,
  SearchOutlined,
  EditOutlined,
  DatabaseOutlined,
  FileTextOutlined,
  ThunderboltOutlined,
  BulbOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  MinusCircleOutlined,
  CopyOutlined,
  ApartmentOutlined,
  AuditOutlined,
} from '@ant-design/icons';
import './PipelineTracePanel.css';

function safeJson(obj) {
  try {
    return JSON.stringify(obj, null, 2);
  } catch {
    return String(obj);
  }
}

function copyText(str) {
  const ta = document.createElement('textarea');
  ta.value = str;
  ta.setAttribute('readonly', '');
  ta.style.cssText = 'position:fixed;left:-9999px;top:-9999px;opacity:0';
  document.body.appendChild(ta);
  ta.select();
  try {
    document.execCommand('copy');
  } catch (_) {
    /* */
  }
  document.body.removeChild(ta);
  try {
    navigator.clipboard?.writeText(str);
  } catch (_) {
    /* */
  }
}

function stepIcon(id) {
  const s = String(id || '');
  if (s.includes('rewrite')) return <EditOutlined />;
  if (s === 'retrieval_query_classic') return <SearchOutlined />;
  if (s === 'retrieval' || s.includes('retrieval')) return <DatabaseOutlined />;
  if (s.includes('context')) return <FileTextOutlined />;
  if (s.includes('llm')) return <ThunderboltOutlined />;
  if (s.includes('thinking')) return <BulbOutlined />;
  if (s.startsWith('agent_narration') || s === 'agent_response') return <BulbOutlined />;
  if (s.startsWith('agent_tool')) return <ThunderboltOutlined />;
  if (s === 'judge' || s.includes('judge')) return <AuditOutlined />;
  if (s.includes('trace') || s.includes('pipeline')) return <ApartmentOutlined />;
  return <ApartmentOutlined />;
}

function statusIcon(status) {
  const st = String(status || '').toLowerCase();
  if (st === 'ok') return <CheckCircleOutlined className="pipeline-trace-inline__status pipeline-trace-inline__status--ok" />;
  if (st === 'error') return <CloseCircleOutlined className="pipeline-trace-inline__status pipeline-trace-inline__status--err" />;
  return <MinusCircleOutlined className="pipeline-trace-inline__status pipeline-trace-inline__status--skip" />;
}

function formatDetailForHighlight(detail) {
  if (detail == null) return '';
  if (typeof detail === 'string') return detail;
  return safeJson(detail);
}

/**
 * Collapsed summary + expandable timeline for a completed pipeline trace.
 *
 * @param {object|null} trace — { version?, steps?: [...] }
 * @param {'default'|'aboveBubble'} [variant] — layout hint for Chat vs Integration
 */
export default function PipelineTracePanel({ trace, variant = 'default' }) {
  const [rootOpen, setRootOpen] = useState(false);
  const [openStepId, setOpenStepId] = useState(null);

  const steps = trace?.steps;
  const hasSteps = Array.isArray(steps) && steps.length > 0;

  const summary = useMemo(() => {
    if (!hasSteps) return '';
    const n = steps.length;
    const wall =
      trace?.pipeline_wall_seconds != null
        ? Number(trace.pipeline_wall_seconds)
        : null;
    const sumDur = steps.reduce((acc, s) => {
      const d = s.duration_seconds;
      return acc + (typeof d === 'number' && !Number.isNaN(d) ? d : 0);
    }, 0);
    const totalS = wall != null && !Number.isNaN(wall) ? wall : sumDur;
    const timePart =
      totalS > 0
        ? ` · ${totalS < 10 ? totalS.toFixed(1) : Math.round(totalS)} s`
        : '';
    return `${n} step${n === 1 ? '' : 's'}${timePart}`;
  }, [hasSteps, steps, trace]);

  const toggleStep = useCallback((id) => {
    setOpenStepId((prev) => (prev === id ? null : id));
  }, []);

  if (!hasSteps) return null;

  const rootClass =
    variant === 'aboveBubble'
      ? 'pipeline-trace-inline pipeline-trace-inline--above-bubble'
      : 'pipeline-trace-inline';

  return (
    <div className={rootClass}>
      <button
        type="button"
        className="pipeline-trace-inline__summary"
        aria-expanded={rootOpen}
        onClick={() => setRootOpen((o) => !o)}
      >
        <span className="pipeline-trace-inline__summary-text">{summary}</span>
        <motion.span
          className="pipeline-trace-inline__chevron"
          animate={{ rotate: rootOpen ? 90 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <RightOutlined />
        </motion.span>
      </button>

      <AnimatePresence initial={false}>
        {rootOpen && (
          <motion.div
            key="body"
            className="pipeline-trace-inline__body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22 }}
          >
            <div className="pipeline-trace-inline__timeline">
              {steps.map((s, i) => {
                const id = String(s.id ?? i);
                const isLast = i === steps.length - 1;
                const stepOpen = openStepId === id;
                const hasDetail =
                  s.detail != null &&
                  (typeof s.detail === 'object'
                    ? Object.keys(s.detail).length > 0
                    : String(s.detail).trim().length > 0);

                return (
                  <div key={id} className="pipeline-trace-inline__step">
                    <div className="pipeline-trace-inline__rail">
                      <span className="pipeline-trace-inline__rail-icon">{stepIcon(s.id)}</span>
                      {!isLast && <span className="pipeline-trace-inline__rail-line" />}
                    </div>
                    <div className="pipeline-trace-inline__step-main">
                      <button
                        type="button"
                        className={`pipeline-trace-inline__step-head ${hasDetail ? 'pipeline-trace-inline__step-head--interactive' : ''}`}
                        onClick={() => hasDetail && toggleStep(id)}
                        disabled={!hasDetail}
                      >
                        <span className="pipeline-trace-inline__step-title">
                          {s.title || id}
                          {s.id === 'judge' && s.detail?.faithfulness != null && (
                            <span style={{ marginLeft: 8, fontWeight: 500, opacity: 0.85 }}>
                              F:{s.detail.faithfulness} R:{s.detail.relevance}
                            </span>
                          )}
                        </span>
                        {s.status && (
                          <span className="pipeline-trace-inline__step-meta">
                            {statusIcon(s.status)}
                            <span className="pipeline-trace-inline__step-status-label">{s.status}</span>
                          </span>
                        )}
                        {(s.duration_seconds != null || s.duration_ms != null) && (
                          <span className="pipeline-trace-inline__step-dur">
                            {s.duration_seconds != null
                              ? `${Number(s.duration_seconds) < 10 ? Number(s.duration_seconds).toFixed(1) : Math.round(Number(s.duration_seconds))} s`
                              : `${s.duration_ms} ms`}
                          </span>
                        )}
                        {hasDetail && (
                          <span className="pipeline-trace-inline__step-chevron">
                            {stepOpen ? <DownOutlined /> : <RightOutlined />}
                          </span>
                        )}
                      </button>

                      <AnimatePresence initial={false}>
                        {stepOpen && hasDetail && (
                          <motion.div
                            key="detail"
                            className="pipeline-trace-inline__detail"
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.18 }}
                          >
                            <div className="pipeline-trace-inline__detail-toolbar">
                              <button
                                type="button"
                                className="pipeline-trace-inline__copy"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  copyText(formatDetailForHighlight(s.detail));
                                }}
                              >
                                <CopyOutlined /> Copy
                              </button>
                            </div>
                            <div className="pipeline-trace-inline__code-wrap">
                              {typeof s.detail === 'string' ? (
                                <pre className="pipeline-trace-inline__pre">{s.detail}</pre>
                              ) : (
                                <SyntaxHighlighter
                                  language="json"
                                  style={prism}
                                  customStyle={{
                                    margin: 0,
                                    padding: '10px 12px',
                                    borderRadius: 8,
                                    fontSize: 12,
                                    lineHeight: 1.45,
                                    maxHeight: 280,
                                    overflow: 'auto',
                                  }}
                                  codeTagProps={{ className: 'pipeline-trace-inline__code-tag' }}
                                >
                                  {formatDetailForHighlight(s.detail)}
                                </SyntaxHighlighter>
                              )}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
